/**
 * Character generation (§5). Two paths, both seeded, both landing on the
 * same sheet:
 *
 *  - Guided: three or four questions that read as fiction. Each answer
 *    carries flaws and strengths behind the scenes, and one question picks
 *    the dependent. The player thinks they're describing a person; they're
 *    building a sheet.
 *  - Random roll: the same thing played back as a paragraph. Rerollable.
 *    Strictly better than point-buy by a fixed bonus, so it's tempting.
 *
 * The flaw budget: flaws carry a value, strengths carry a cost, and a sheet
 * is legal only if cost ≤ value. Stat points are priced cheap relative to
 * relationships and connections; `content/chargen/chargen.json` sets the
 * prices and a test holds the ordering.
 *
 * `buildSheet` is the point-buy API and the thing both paths call. Nothing
 * here touches the world: a sheet is applied by `sim/generate.ts`.
 */

import type { ChargenData, ChargenEffect, Content, FlawDef, StrengthDef } from './content.js';
import { Rng, type Seedable } from './rng.js';
import type { Flaw, RelationshipAxes, StatName, StatValue, Stats } from './types/index.js';

export interface SheetObligation {
  readonly to: string;
  readonly amount: number;
  readonly deadlineDays: number;
}

export interface SheetSecret {
  readonly id: string;
  readonly content: string;
  readonly consequencesIfExposed: string;
}

/** What generation hands the world builder. Ids are content ids, unbranded. */
export interface CharacterSheet {
  readonly path: 'guided' | 'random';
  readonly name: string;
  readonly summary: string;
  readonly stats: Stats;
  readonly flaws: readonly Flaw[];
  readonly flawIds: readonly string[];
  readonly strengthIds: readonly string[];
  readonly dependent: string;
  readonly money: number;
  readonly relationships: Readonly<Record<string, Partial<RelationshipAxes>>>;
  readonly knownLocations: readonly string[];
  readonly obligations: readonly SheetObligation[];
  readonly secrets: readonly SheetSecret[];
  readonly budget: Budget;
}

export interface Budget {
  /** Points the flaws bought. */
  readonly value: number;
  /** Points the strengths cost. */
  readonly cost: number;
  /** `value` plus any bonus (the random roll's edge). */
  readonly allowance: number;
}

const STAT_NAMES: readonly StatName[] = ['nerve', 'read', 'presence', 'body'];

function clampStat(n: number): StatValue {
  return Math.max(1, Math.min(5, n)) as StatValue;
}

function flawById(data: ChargenData, id: string): FlawDef {
  const flaw = data.flaws.find((f) => f.id === id);
  if (!flaw) throw new Error(`Unknown flaw "${id}"`);
  return flaw;
}

function strengthById(data: ChargenData, id: string): StrengthDef {
  const strength = data.strengths.find((s) => s.id === id);
  if (!strength) throw new Error(`Unknown strength "${id}"`);
  return strength;
}

export function budgetOf(data: ChargenData, flawIds: readonly string[], strengthIds: readonly string[], bonus = 0): Budget {
  const value = flawIds.reduce((sum, id) => sum + flawById(data, id).value, 0);
  const cost = strengthIds.reduce((sum, id) => sum + strengthById(data, id).cost, 0);
  return { value, cost, allowance: value + bonus };
}

export interface SheetSpec {
  readonly path: 'guided' | 'random';
  readonly name: string;
  readonly flawIds: readonly string[];
  readonly strengthIds: readonly string[];
  readonly dependent: string;
  readonly summary: string;
  /** Extra points beyond what the flaws bought. Zero for point-buy and guided. */
  readonly bonus?: number;
}

/**
 * Point-buy. Validates the budget and applies every effect to a fresh
 * sheet. Throws on an illegal sheet: this is the one place the rule lives.
 */
export function buildSheet(content: Content, spec: SheetSpec): CharacterSheet {
  const data = content.chargen;
  const flawIds = [...new Set(spec.flawIds)];
  if (flawIds.length !== spec.flawIds.length) throw new Error('A flaw cannot be taken twice');

  const seen = new Map<string, number>();
  for (const id of spec.strengthIds) {
    const s = strengthById(data, id);
    const n = (seen.get(id) ?? 0) + 1;
    seen.set(id, n);
    if (n > 1 && !s.repeatable) throw new Error(`Strength "${id}" cannot be bought twice`);
  }

  const budget = budgetOf(data, flawIds, spec.strengthIds, spec.bonus ?? 0);
  if (budget.cost > budget.allowance) {
    throw new Error(`Over budget: strengths cost ${budget.cost}, flaws bought ${budget.allowance}`);
  }
  if (!content.npcs.some((n) => n.id === spec.dependent && n.dependent)) {
    throw new Error(`"${spec.dependent}" cannot be a dependent`);
  }

  const stats: Record<StatName, number> = { ...data.baseStats };
  const relationships: Record<string, Partial<RelationshipAxes>> = {};
  const knownLocations = new Set<string>();
  const obligations: SheetObligation[] = [];
  const secrets: SheetSecret[] = [];
  let money = data.startingMoney;

  const apply = (effect: ChargenEffect) => {
    switch (effect.kind) {
      case 'stat':
        stats[effect.stat] += effect.delta;
        break;
      case 'relationship': {
        const current = relationships[effect.npc] ?? {};
        const merged: { -readonly [K in keyof RelationshipAxes]?: number } = { ...current };
        for (const axis of ['trust', 'respect', 'fear', 'warmth'] as const) {
          const d = effect.axes[axis];
          if (d !== undefined) merged[axis] = (merged[axis] ?? 0) + d;
        }
        relationships[effect.npc] = merged;
        break;
      }
      case 'money':
        money += effect.amount;
        break;
      case 'known_location':
        knownLocations.add(effect.location);
        break;
      case 'obligation':
        obligations.push({ to: effect.to, amount: effect.amount, deadlineDays: effect.deadlineDays });
        break;
      case 'secret':
        secrets.push({ id: effect.id, content: effect.content, consequencesIfExposed: effect.consequencesIfExposed });
        break;
    }
  };

  const flaws: Flaw[] = [];
  for (const id of flawIds) {
    const flaw = flawById(data, id);
    flaws.push({ kind: flaw.id, detail: flaw.detail });
    flaw.effects.forEach(apply);
  }
  for (const id of spec.strengthIds) strengthById(data, id).effects.forEach(apply);

  const finalStats = Object.fromEntries(STAT_NAMES.map((s) => [s, clampStat(stats[s])])) as Stats;

  return {
    path: spec.path,
    name: spec.name,
    summary: spec.summary,
    stats: finalStats,
    flaws,
    flawIds,
    strengthIds: [...spec.strengthIds],
    dependent: spec.dependent,
    money: Math.max(0, money),
    relationships,
    knownLocations: [...knownLocations],
    obligations,
    secrets,
    budget,
  };
}

// ---------------------------------------------------------------------------
// Guided
// ---------------------------------------------------------------------------

/** Answers by question id → option id. */
export type GuidedAnswers = Readonly<Record<string, string>>;

export function guidedQuestions(content: Content) {
  return content.chargen.questions;
}

function seededName(data: ChargenData, seed: Seedable): string {
  const first = new Rng(seed).fork('name').pick(data.firstNames);
  return `${first} ${data.surname}`;
}

/**
 * Turn answers into a sheet. Every question must be answered with one of
 * its options. Flaws repeat-proof by construction (content keeps them
 * unique across options); the budget is checked in `buildSheet`.
 */
export function answerGuided(content: Content, seed: Seedable, answers: GuidedAnswers, name?: string): CharacterSheet {
  const data = content.chargen;
  const flawIds: string[] = [];
  const strengthIds: string[] = [];
  const lines: string[] = [];
  let dependent: string | null = null;

  for (const question of data.questions) {
    const chosen = answers[question.id];
    const option = question.options.find((o) => o.id === chosen);
    if (!option) throw new Error(`Question "${question.id}" needs one of: ${question.options.map((o) => o.id).join(', ')}`);
    flawIds.push(...option.flaws);
    strengthIds.push(...option.strengths);
    if (option.dependent !== undefined) dependent = option.dependent;
    lines.push(option.text);
  }
  if (dependent === null) throw new Error('No answer chose a dependent');

  return buildSheet(content, {
    path: 'guided',
    name: name ?? seededName(data, seed),
    flawIds,
    strengthIds,
    dependent,
    summary: lines.join(' '),
  });
}

// ---------------------------------------------------------------------------
// Random roll
// ---------------------------------------------------------------------------

/** How many times one stat may be raised in a single roll. Keeps 5s rare (§5). */
const MAX_STAT_RAISES_PER_ROLL = 2;

/**
 * The same thing played back. Deterministic in (seed, roll): rerolling is
 * asking for the next index, and every index is stable forever.
 */
export function rollCharacter(content: Content, seed: Seedable, roll = 0): CharacterSheet {
  const data = content.chargen;
  const rng = new Rng(seed).fork(`roll:${roll}`);

  const candidates = content.npcs.filter((n) => n.dependent);
  const dependentCard = rng.pick(candidates);

  const count = rng.intInclusive(data.randomRollFlaws.min, Math.min(data.randomRollFlaws.max, data.flaws.length));
  const flaws = rng.sample(data.flaws, count);
  const flawIds = flaws.map((f) => f.id);

  const allowance = flaws.reduce((sum, f) => sum + f.value, 0) + data.randomRollBonus;
  const strengthIds: string[] = [];
  const bought = new Map<string, number>();
  const statRaises: Record<StatName, number> = { nerve: 0, read: 0, presence: 0, body: 0 };
  let spent = 0;

  // Walk the strengths in a seeded order, buying what fits, until nothing does.
  let progress = true;
  while (progress) {
    progress = false;
    for (const s of rng.shuffle(data.strengths)) {
      if (spent + s.cost > allowance) continue;
      const times = bought.get(s.id) ?? 0;
      if (times >= 1 && !s.repeatable) continue;
      const stat = s.effects.find((e) => e.kind === 'stat');
      if (stat && stat.kind === 'stat') {
        if (statRaises[stat.stat] >= MAX_STAT_RAISES_PER_ROLL) continue;
        statRaises[stat.stat] += 1;
      }
      bought.set(s.id, times + 1);
      strengthIds.push(s.id);
      spent += s.cost;
      progress = true;
    }
  }

  const lines = [
    `You are ${seededName(data, `${String(seed)}:${roll}`)}.`,
    ...flaws.map((f) => f.rollLine),
    ...[...bought.keys()].map((id) => data.strengths.find((s) => s.id === id)!.rollLine),
    dependentCard.dependent!.rollLine,
  ];

  return buildSheet(content, {
    path: 'random',
    name: seededName(data, `${String(seed)}:${roll}`),
    flawIds,
    strengthIds,
    dependent: dependentCard.id,
    summary: lines.join(' '),
    bonus: data.randomRollBonus,
  });
}
